import globalConfig from '@utiles/globalConfig';

/** session 缓存：同一 SPA 会话内复用灰度结果，登出清除 */
export const SUPPORT_CENTER_VERSION_STORAGE_KEY =
  'officialSupport.supportCenterVersion';

export const SUPPORT_CENTER_VERSION = {
  V1: 1,
  V2: 2
};

const readStorage = () => {
  try {
    return window.sessionStorage.getItem(SUPPORT_CENTER_VERSION_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStorage = value => {
  try {
    window.sessionStorage.setItem(
      SUPPORT_CENTER_VERSION_STORAGE_KEY,
      String(value)
    );
  } catch (e) {
    // Safari 隐私模式等：忽略，vuex 仍可用
  }
};

const removeStorage = () => {
  try {
    window.sessionStorage.removeItem(SUPPORT_CENTER_VERSION_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

/** 仅接受 1/2，其它按 V1 */
export const normalizeSupportCenterVersion = value =>
  Number(value) === SUPPORT_CENTER_VERSION.V2
    ? SUPPORT_CENTER_VERSION.V2
    : SUPPORT_CENTER_VERSION.V1;

/** 联调 / 预览强制 V2；线上入口不要给全量用户拼 version=2 */
export const isForceSupportCenterV2ByUrl = () =>
  String(globalConfig.getHrefParam('version') || '') === '2';

export const readCachedSupportCenterVersion = () =>
  normalizeSupportCenterVersion(readStorage());

export const writeCachedSupportCenterVersion = version => {
  const v = normalizeSupportCenterVersion(version);
  writeStorage(v);
  return v;
};

export const clearCachedSupportCenterVersion = () => {
  removeStorage();
};

/**
 * URL version=2 优先；否则用接口值（失败/空视为 1）
 */
export const resolveSupportCenterVersion = apiVersion => {
  if (isForceSupportCenterV2ByUrl()) {
    return SUPPORT_CENTER_VERSION.V2;
  }
  return normalizeSupportCenterVersion(apiVersion);
};

/** store 初始化 / 同步读：URL 强制或 session 缓存 */
export const readInitialSupportCenterVersion = () =>
  resolveSupportCenterVersion(readCachedSupportCenterVersion());

/** 写入 session 缓存并返回最终页面版本 */
export const persistSupportCenterVersion = apiVersion => {
  const v = resolveSupportCenterVersion(apiVersion);
  writeCachedSupportCenterVersion(v);
  return v;
};

/**
 * 登出：清缓存；URL 强制时仍保持 V2
 */
export const resetSupportCenterVersionCache = () => {
  if (isForceSupportCenterV2ByUrl()) {
    return persistSupportCenterVersion(SUPPORT_CENTER_VERSION.V2);
  }
  clearCachedSupportCenterVersion();
  return persistSupportCenterVersion(SUPPORT_CENTER_VERSION.V1);
};

/**
 * 同步判定（actions / 路由 query 组装）。
 * 模板展示请用 vuex getter isSupportCenterV2Page，否则登录 1→2 不会刷新。
 */
export const isSupportCenterV2 = () =>
  readInitialSupportCenterVersion() === SUPPORT_CENTER_VERSION.V2;

/** 非 prod（本地 / *-test.）才透传 mock_to_account */
export const shouldPassMockToAccount = () => {
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV !== 'production';
  }
  const host = window.location.hostname || '';
  if (host === 'localhost' || host === '127.0.0.1') {
    return true;
  }
  if (/-test\./i.test(host)) {
    return true;
  }
  return process.env.NODE_ENV !== 'production';
};

export const getSupportCenterApiUrl = (action, requestLang) => {
  const lang = requestLang || 'en-US';
  const path = `/gsupport/${lang}/supportCenter/${action}`;
  if (process.env.NODE_ENV === 'development') {
    return `/gsupport${path}`;
  }
  return `//gsupport.${globalConfig.getMainHost()}${path}`;
};

export const getSupportCenterAuthHeaders = () => {
  const headers = {};
  const token =
    typeof globalConfig.getCookie === 'function'
      ? globalConfig.getCookie('X_Passport_Token')
      : '';
  if (token) {
    headers.X_Passport_Token = token;
  }
  return headers;
};

export const buildSupportCenterVersionParams = ({
  requestLang,
  eInfo,
  mockToAccount
} = {}) => {
  const params = {
    appLanguage: requestLang || 'en-US',
    language: requestLang || 'en-US'
  };
  if (eInfo) {
    params.e_info = eInfo;
  }
  if (shouldPassMockToAccount() && mockToAccount) {
    params.mock_to_account = mockToAccount;
  }
  return params;
};
